import type { Permission, Role, WebsiteTab } from '../../../../hooks/useValidateSession';
import type { UserPermission } from '../../../../hooks/useEditDefaultPermissions';

export const ROLES: Array<Role> = [
    'executive',
    'gm',
    'central-parts',
    'parts-manager',
    'estimator',
    'technician',
    'invoicing',
    'it',
    'other',
];

export const ROLE_LABELS: Record<Role, string> = {
    executive: 'Executive',
    gm: 'GM',
    'central-parts': 'Central Parts',
    'parts-manager': 'Parts Manager',
    estimator: 'Estimator',
    technician: 'Technician',
    invoicing: 'Invoicing',
    it: 'IT',
    other: 'Other',
};

export const TABS: Array<WebsiteTab> = [
    'dashboard',
    'actvity',
    'ro-manager',
    'reporting',
    'invoicing',
    'inventory',
    'ask-carl',
    'tasks',
];

/**
 * Display names match the sidebar, which is the product truth. The KEYS are
 * persisted session data (including the historical `actvity` typo and
 * `ro-manager`), so they never change — only the labels do.
 */
export const TAB_LABELS: Record<WebsiteTab, string> = {
    dashboard: 'Orders',
    actvity: 'Exports',
    'ro-manager': 'Home',
    reporting: 'Reporting',
    invoicing: 'Invoicing',
    inventory: 'Inventory',
    'ask-carl': 'Ask Carl',
    tasks: 'Tasks',
};

export const PERMISSIONS: Array<Permission> = [
    'announcements',
    'place-order',
    'place-ev-order',
    'order-flagged-parts',
    'change-selections',
    'change-unflagged',
    'clear-flag',
    'add-option-note',
    'complete-uncomplete',
    'archive-unarchive',
    'request-quotes',
    'update-order-details',
    'include-exclude-parts',
    'toggle-flag',
    'flag-order',
    'manual-rekey-action',
    'export-selections',
    'accept-supplier-changes',
    'general-reporting',
    'invoicing-reporting',
    'inventory-reporting',
    'it-settings',
    'settings',
    'preferences',
    'invoicing',
    'inventory',
];

export const PERMISSION_LABELS: Record<Permission, string> = {
    announcements: 'Announcements',
    'place-order': 'Place Order',
    'place-ev-order': 'Place EV Order',
    'order-flagged-parts': 'Order Flagged Parts',
    'change-selections': 'Change Selections',
    'change-unflagged': 'Change Unflagged Selections',
    'clear-flag': 'Clear Flag',
    'add-option-note': 'Add Option Note',
    'complete-uncomplete': 'Complete / Uncomplete',
    'archive-unarchive': 'Archive / Unarchive',
    'request-quotes': 'Request Quotes',
    'update-order-details': 'Update Order Details',
    'include-exclude-parts': 'Include / Exclude Parts',
    'toggle-flag': 'Toggle Flag',
    'flag-order': 'Flag Order',
    'manual-rekey-action': 'Manual Re-Key Action',
    'export-selections': 'Export Selections',
    'accept-supplier-changes': 'Accept Supplier Changes',
    'general-reporting': 'General Reporting',
    'invoicing-reporting': 'Invoicing Reporting',
    'inventory-reporting': 'Inventory Reporting',
    'it-settings': 'IT Settings',
    settings: 'Settings',
    preferences: 'Preferences',
    invoicing: 'Invoicing',
    inventory: 'Inventory',
};

export type DefaultPermissionsMatrix = Record<Role, UserPermission>;

export const buildMatrix = (
    initial?: Partial<Record<Role, UserPermission>>,
): DefaultPermissionsMatrix =>
    ROLES.reduce((matrix, role) => {
        matrix[role] = {
            permissions: [...(initial?.[role]?.permissions ?? [])],
            tabs: [...(initial?.[role]?.tabs ?? [])],
        };
        return matrix;
    }, {} as DefaultPermissionsMatrix);

export const toggleValue = <T,>(values: Array<T>, value: T): Array<T> =>
    values.includes(value) ? values.filter((v) => v !== value) : [...values, value];

export const matrixEquals = (a: DefaultPermissionsMatrix, b: DefaultPermissionsMatrix): boolean =>
    ROLES.every((role) => {
        const left = a[role];
        const right = b[role];
        const sameLength =
            left.tabs.length === right.tabs.length &&
            left.permissions.length === right.permissions.length;
        return (
            sameLength &&
            left.tabs.every((tab) => right.tabs.includes(tab)) &&
            left.permissions.every((permission) => right.permissions.includes(permission))
        );
    });

export const copyUserPermission = (value?: UserPermission): UserPermission => ({
    permissions: [...(value?.permissions ?? [])],
    tabs: [...(value?.tabs ?? [])],
});

const sameStringSets = (a: Array<string>, b: Array<string>) => {
    if (a.length !== b.length) return false;
    const other = new Set(b);
    return a.every((value) => other.has(value));
};

/** Order-insensitive compare of one role's or user's tabs and permissions. */
export const sameUserPermission = (a: UserPermission, b: UserPermission) =>
    sameStringSets(a.tabs, b.tabs) && sameStringSets(a.permissions, b.permissions);

/**
 * Tabs and permissions the org has turned on, filtered from the full catalog.
 * Both the defaults matrix and the per-user editors hide the same feature-gated
 * rows, so they share this instead of each re-listing the gates.
 */
/**
 * Main-app areas an invoicing org can grant. Orders, Exports, Home, Inventory,
 * and Ask Carl are not part of this account type.
 */
const INVOICING_ORG_TABS: Array<WebsiteTab> = ['reporting', 'invoicing'];

/**
 * Permission rows kept for an invoicing org. Everything else in the catalog is
 * procurement, inventory, or IT setup, and the row is removed.
 */
const INVOICING_ORG_PERMISSIONS: Array<Permission> = [
    'announcements',
    'invoicing-reporting',
    'settings',
    'preferences',
    'invoicing',
];

export const visibleMatrixRows = (gates: {
    invoicingEnabled: boolean;
    inventoryEnabled: boolean;
    /** Invoicing-only org: drop rows that account type cannot use. */
    invoicingOnly?: boolean;
}): { tabs: Array<WebsiteTab>; permissions: Array<Permission> } => {
    if (gates.invoicingOnly) {
        return {
            tabs: INVOICING_ORG_TABS.filter((tab) => tab !== 'invoicing' || gates.invoicingEnabled),
            permissions: INVOICING_ORG_PERMISSIONS.filter((permission) => {
                if (permission === 'invoicing' || permission === 'invoicing-reporting') {
                    return gates.invoicingEnabled;
                }
                return true;
            }),
        };
    }
    return {
        tabs: TABS.filter((tab) => {
            if (tab === 'invoicing') return gates.invoicingEnabled;
            if (tab === 'inventory') return gates.inventoryEnabled;
            return true;
        }),
        permissions: PERMISSIONS.filter((permission) => {
            if (permission === 'invoicing' || permission === 'invoicing-reporting') {
                return gates.invoicingEnabled;
            }
            if (permission === 'inventory' || permission === 'inventory-reporting') {
                return gates.inventoryEnabled;
            }
            return true;
        }),
    };
};
